import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import PowerSelect from 'ember-power-select/components/power-select';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, gt, not } from 'ember-truth-helpers';
import DateRanger from 'garaje/components/date-ranger';
import EventLogDrawer from 'garaje/components/event-log-drawer';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import SortableHeaderColumn from 'garaje/components/sortable-header-column';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import UiHeader from 'garaje/components/ui/header';
import UiPaginationCombined from 'garaje/components/ui-pagination/combined';
import UiSearch from 'garaje/components/ui-search';
import dateFormat from 'garaje/helpers/date-format';
import highlightText from 'garaje/helpers/highlight-text';
import { type PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type EventLogModel from 'garaje/models/event-log';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type SessionService from 'garaje/services/session';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { cached } from 'tracked-toolbox';

interface EventLogTableSignature {
  Args: {
    isGlobal?: boolean;
    page: number;
    search: string;
    sort: string;
    locationId?: number;
    changePage: (page: number) => void;
    changeSearch: (search: string) => void;
    changeSort: (sort: string) => void;
    changeLocation: (locationId: number | null) => void;
  };
}

interface LocationOption {
  disabled: boolean;
  location: SkinnyLocationModel;
}

const PAGE_SIZE = 25;

export function formatTimeWithTimezone(date: Date): string {
  return date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

export default class EventLogTable extends Component<EventLogTableSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: Store;
  @service declare session: SessionService;
  @service declare state: StateService;
  @service declare router: RouterService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare metrics: MetricsService;

  @tracked entries: EventLogModel[] = [];
  @tracked totalCount = 0;
  @tracked pageCount = 0;
  @tracked locations: SkinnyLocationModel[] = [];
  @tracked selectedEvent: EventLogModel | null = null;
  @tracked isDrawerOpen = false;
  @tracked dateFrom: Date = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  })();
  @tracked dateTo: Date = new Date();

  constructor(owner: unknown, args: EventLogTableSignature['Args']) {
    super(owner, args);
    if (this.isGlobal) {
      void this.loadLocations.perform();
    }
    void this.loadData.perform();
  }

  get isGlobal(): boolean {
    return this.args.isGlobal ?? false;
  }

  get currentPage(): number {
    return this.args.page ?? 1;
  }

  get searchQuery(): string {
    return this.args.search ?? '';
  }

  get sortField(): string {
    const [field = 'event_timestamp'] = (this.args.sort ?? 'event_timestamp:desc').split(':');
    return field;
  }

  get sortDirection(): 'asc' | 'desc' {
    const [, direction] = (this.args.sort ?? 'event_timestamp:desc').split(':');
    return direction === 'asc' ? 'asc' : 'desc';
  }

  // ember-power-select disables any object with a `disabled: true` property
  // by wrapping locations in objects with `disabled: false`, we ensure they are not treated as disabled
  @cached
  get locationOptions(): Array<LocationOption> {
    return this.locations.map((location) => ({ disabled: false, location }));
  }

  get selectedLocation(): LocationOption | null {
    if (!this.args.locationId) return null;
    return this.locationOptions.find((option) => option.location.id === this.args.locationId!.toString()) || null;
  }

  get pageTitle(): string {
    if (this.isGlobal) {
      return 'Event log';
    }
    const locationName = this.state.currentLocation?.name;
    return locationName ? `Event log for ${locationName}` : 'Event log';
  }

  loadData = restartableTask(async () => {
    try {
      const companyId = this.session.companyId;
      if (!companyId) {
        throw new Error('No company ID available');
      }

      const companyIdNum = parseInt(companyId, 10);

      const from = (this.currentPage - 1) * PAGE_SIZE;
      const queryParams: Record<string, unknown> = {
        companyId: companyIdNum,
        from,
        size: PAGE_SIZE,
        sort: [`${this.sortField}:${this.sortDirection}`],
      };

      if (this.searchQuery) {
        queryParams['searchText'] = this.searchQuery;
      }

      if (this.dateFrom) {
        // Convert date to timestamp
        queryParams['fromTimestamp'] = this.dateFrom.getTime().toString();
      }

      if (this.dateTo) {
        // Convert date to timestamp, set to end of day
        const endOfDay = new Date(this.dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        queryParams['toTimestamp'] = endOfDay.getTime().toString();
      }

      if (!this.isGlobal && this.state.currentLocation?.id) {
        queryParams['locationId'] = this.state.currentLocation.id;
      } else if (this.isGlobal && this.args.locationId) {
        queryParams['locationId'] = this.args.locationId;
      }

      const result = <PaginatedRecordArray<EventLogModel>>await this.store.query('event-log', queryParams);

      this.entries = result.slice();
      this.totalCount = result.meta?.total || 0;
      this.pageCount = Math.ceil(this.totalCount / PAGE_SIZE);
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error loading event log data', {
        details: parseErrorForDisplay(error),
      });
      this.entries = [];
      this.totalCount = 0;
      this.pageCount = 0;
    }
  });

  loadLocations = restartableTask(async () => {
    try {
      // Ensure locations are loaded
      if (!this.skinnyLocations.isLoaded) {
        await this.skinnyLocations.loadAllTask.perform();
      }
      this.locations = this.skinnyLocations.content;
    } catch (_error) {
      this.locations = [];
    }
  });

  searchTask = restartableTask(async () => {
    await timeout(300); // Debounce search
    void this.loadData.perform();
  });

  @action
  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const searchValue = target.value;

    // track search queries (only non-empty searches to avoid noise)
    if (searchValue.trim().length > 0) {
      this.metrics.trackEvent('Event Log Search Performed', {
        search_query: searchValue.trim(),
        context: this.isGlobal ? 'global_event_log' : 'local_event_log',
        scope: this.isGlobal ? 'global' : 'location',
      });
    }

    this.args.changeSearch(searchValue);
    void this.searchTask.perform();
  }

  @action
  onSearchClear(): void {
    this.metrics.trackEvent('Event Log Search Cleared', {
      context: this.isGlobal ? 'global_event_log' : 'local_event_log',
      scope: this.isGlobal ? 'global' : 'location',
    });

    this.args.changeSearch('');
    void this.loadData.perform();
  }

  @action
  setPageNumber(page: number): void {
    this.args.changePage(page);
    void this.loadData.perform();
  }

  @action
  sortAction(sortField: string, direction: 'asc' | 'desc'): void {
    const sortValue = `${sortField}:${direction}`;
    this.args.changeSort(sortValue);
    void this.loadData.perform();
  }

  @action
  onLocationChange(option: LocationOption | null): void {
    const locationId = option?.location ? parseInt(option.location.id, 10) : null;

    // track location filter changes (only on global)
    this.metrics.trackEvent('Event Log Location Filter Changed', {
      selected_location_id: locationId,
      selected_location_name: option?.location?.name || null,
      filter_cleared: locationId === null,
      context: 'global_event_log',
      scope: 'global',
    });

    this.args.changeLocation(locationId);
    void this.loadData.perform();
  }

  @action
  updateDateRange(startDate: Date, endDate: Date): void {
    this.dateFrom = startDate;
    this.dateTo = endDate;
    this.args.changePage(1); // Reset to first page
    void this.loadData.perform();
  }

  @action
  openDrawer(event: EventLogModel): void {
    this.selectedEvent = event;
    this.isDrawerOpen = true;
  }

  @action
  closeDrawer(): void {
    this.isDrawerOpen = false;
    this.selectedEvent = null;
  }

  <template>
    <div class="flex items-center">
      <div class="float-left box-border w-6/12">
        <UiHeader>
          {{this.pageTitle}}
        </UiHeader>
      </div>
      <div class="float-left box-border w-6/12 text-right relative">
        <UiSearch
          data-test-search
          @inputAriaLabel="Search event log"
          @inputId="events-search"
          @placeholder="Search event log"
          @showClearButton={{gt this.searchQuery.length 0}}
          @onInput={{this.onSearchInput}}
          @onClear={{this.onSearchClear}}
          @value={{this.searchQuery}}
        />
      </div>
    </div>

    <div class="pb-4 mt-6 flex items-center gap-4">
      <div class="flex items-center">
        <DateRanger
          data-test-date-filter-button
          data-test-date-range-picker
          @startDate={{this.dateFrom}}
          @endDate={{this.dateTo}}
          @onDateSelected={{this.updateDateRange}}
          @onDateRangeSelected={{this.updateDateRange}}
        />
      </div>

      {{#if this.isGlobal}}
        <div class="flex items-center">
          <PowerSelect
            data-test-location-selector
            class="w-64 !mb-0"
            @options={{this.locationOptions}}
            @selected={{this.selectedLocation}}
            @onChange={{this.onLocationChange}}
            @placeholder="All locations"
            @allowClear={{true}}
            @searchEnabled={{true}}
            @searchField="location.name"
            as |option|
          >
            <span data-test-location-option="{{option.location.id}}">{{option.location.name}}</span>
          </PowerSelect>
        </div>
      {{/if}}
    </div>

    <div class="table table-auto !mt-0 w-full box-border text-carbon-50" data-test-event-log-table>
      <div class="table-row">
        <div class="table-cell border-b text-sm p-3 px-5 align-middle font-medium min-w-32 whitespace-nowrap">
          <SortableHeaderColumn
            @name="Date and time"
            @sortField="event_timestamp"
            @sortAction={{this.sortAction}}
            @isActive={{eq this.sortField "event_timestamp"}}
            @sortDirection={{this.sortDirection}}
          />
        </div>
        <div class="table-cell border-b text-sm p-3 px-5 align-middle font-medium min-w-28 whitespace-nowrap">
          Performed by
        </div>
        <div class="table-cell border-b text-sm p-3 px-5 align-middle font-medium">
          Action
        </div>
        {{#if this.isGlobal}}
          <div class="table-cell border-b text-sm p-3 px-5 align-middle font-medium">
            Location
          </div>
        {{/if}}
      </div>

      {{#if this.loadData.isRunning}}
        <LoadingSpinnerPulse data-test-loading-spinner class="pt-12" />
      {{else}}
        {{#each this.entries as |entry|}}
          <div
            class="table-row hover:bg-carbon-5 cursor-pointer"
            data-test-event-log-row
            data-event-type="{{entry.eventType}}"
            data-location-id="{{entry.locationId}}"
            {{on "click" (fn this.openDrawer entry)}}
          >
            <div
              class="table-cell border-b p-3 px-5 align-middle text-sm min-w-32 whitespace-nowrap"
              data-test-event-datetime
            >
              {{#if entry.dateTime}}
                <div class="text-carbon-70">{{dateFormat entry.dateTime "MMM d, yyyy"}}</div>
                <div class="text-carbon-50">{{formatTimeWithTimezone entry.dateTime}}</div>
              {{else}}
                <span class="text-carbon-50">N/A</span>
              {{/if}}
            </div>
            <div
              class="table-cell border-b p-3 px-5 align-middle text-carbon-70 text-base min-w-28 whitespace-nowrap"
              data-test-event-actor
            >
              {{#if entry.actorName}}
                <div class="flex items-center gap-1">
                  {{#if (eq entry.actorType "system")}}
                    <div class="mx-1 w-10 h-10 bg-carbon-10 rounded-full flex items-center justify-center">
                      <span class="flex" style="padding-left: 0.05rem;">
                        {{svgJar "logo-small-red" width="18" height="18"}}
                      </span>
                    </div>
                  {{else}}
                    <MonogramOrPhoto class="!p-0" @name={{entry.actorName}} @medium={{true}} @borderBottom={{false}} />
                  {{/if}}
                  <span style={{if (eq entry.actorType "system") "padding-left: 0.16rem;"}}>
                    {{highlightText entry.actorName this.searchQuery}}
                  </span>
                </div>
              {{else}}
                -
              {{/if}}
            </div>
            <div
              class="table-cell border-b p-3 px-5 align-middle text-sm text-carbon-50"
              data-test-event-type
              data-test-event-description
            >
              <p class="mb-0 text-carbon-70">
                {{#if entry.eventTypeName}}
                  {{highlightText entry.eventTypeName this.searchQuery}}
                {{else}}
                  -
                {{/if}}
              </p>
              {{#if entry.eventDescription}}
                {{highlightText entry.eventDescription this.searchQuery}}
              {{else}}
                -
              {{/if}}
            </div>
            {{#if this.isGlobal}}
              <div class="table-cell border-b p-3 px-5 align-middle text-sm text-carbon-50" data-test-event-location>
                {{entry.location}}
              </div>
            {{/if}}
          </div>
        {{/each}}
      {{/if}}
    </div>

    {{#if (and (not this.loadData.isRunning) (not this.entries.length))}}
      <div class="flex justify-center items-center py-12" data-test-empty-state>
        <div class="text-center text-base text-carbon-50">
          No events found
        </div>
      </div>
    {{/if}}

    {{#if (and (not this.loadData.isRunning) this.entries.length)}}
      <UiPaginationCombined
        data-test-pagination
        @pageCount={{this.pageCount}}
        @pageNumber={{@page}}
        @pageSize={{PAGE_SIZE}}
        @recordCount={{this.totalCount}}
        @setPageNumber={{this.setPageNumber}}
      />
    {{/if}}

    {{#if this.selectedEvent}}
      <EventLogDrawer @event={{this.selectedEvent}} @isOpen={{this.isDrawerOpen}} @onClose={{this.closeDrawer}} />
    {{/if}}
  </template>
}
